/**
 * Elegir a quien se le vende.
 *
 * POR QUE MUESTRA EL SALDO Y LA MORA ANTES DE ELEGIR
 * --------------------------------------------------
 * El momento de asociar un cliente a una venta es el ultimo momento util para
 * decidir si conviene venderle. Si el saldo y el estado de mora aparecieran
 * despues, la informacion llega tarde: la mercaderia ya salio por la puerta.
 *
 * Por eso cada resultado trae cuanto debe, hace cuanto, y si excedio su limite
 * — con el aviso en amarillo o rojo segun corresponda. No bloquea nada: el que
 * decide vender es el dueño, no el sistema. Pero decide sabiendo.
 *
 * ES UNA PALETA DE BUSQUEDA
 * -------------------------
 * Un `Dialog` de Radix (focus trap, Escape, `aria-modal`) en su variante
 * "paleta": anclado arriba, sin titulo visible (queda `sr-only`), con el campo
 * de busqueda como cabecera y la lista de resultados como cuerpo. Centrado, la
 * lista se saldria por abajo mientras se tipea.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { X, UserPlus, AlertTriangle, User, Store, HandCoins } from "lucide-react";

import { convertir, type Moneda } from "@tora/shared";
import { api, mensajeDeError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { IconButton } from "@/components/ui/icon-button";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Importe } from "@/components/money/importe";
import { BadgeMora } from "@/components/cobro/BadgeMora";
import { CobroCliente } from "@/components/cobro/CobroCliente";
import type { DeudorFila } from "@/components/cobro/logica";

interface ClienteConSaldo {
  cliente_id: number;
  nombre: string;
  saldo: string;
  saldo_ars_hoy: string;
  moneda_anclaje: "ARS" | "USD";
  cc_modo: "FIJA" | "USD";
  estado_mora: string;
  limite_credito: string | null;
  limite_moneda: "ARS" | "USD";
  excedido: boolean;
  antiguedad_deuda: number | null;
  // Datos de contacto: los agrego la migracion 0017 a `v_mora_clientes` para
  // que se pueda buscar un cliente por lo que se tenga a mano, no solo por el
  // nombre. Opcionales porque pueden venir vacios en la ficha.
  codigo?: string | null;
  documento?: string | null;
  telefono?: string | null;
  email?: string | null;
}

export interface SelectorClienteProps {
  abierto: boolean;
  onCerrar: () => void;
  onElegir: (id: number, nombre: string) => void;
  /**
   * Abre el alta de cliente SIN salir de la venta, con lo que se busco: el
   * formulario abria vacio y el nombre ya tipeado («No encontré a…») habia que
   * escribirlo de nuevo.
   */
  onCrearNuevo: (buscado: string) => void;
  /**
   * Con que texto abre la busqueda.
   *
   * Lo usa el paso previo de la venta: si alguien empieza a escribir un nombre
   * con el ticket vacio, este selector se abre solo y esa primera tecla tiene
   * que estar ya adentro del campo. Perderla obliga a borrar y volver a
   * empezar, que es justo lo que el atajo venia a evitar.
   */
  textoInicial?: string;
}

/** Minuscula y sin acentos, como `sinAcentos` del backend. */
function plano(v: string): string {
  return v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * El mismo filtro que el servidor (`condicionBusqueda` en db.ts), por los
 * mismos campos: sin acentos, y el telefono y el DNI tambien por sus digitos
 * cuando lo escrito es un numero de al menos tres cifras. Sin eso, «sofia»
 * descartaba a «Sofía» que el servidor si habia traido, y la pantalla decia
 * «No encontré» e invitaba a darla de alta de nuevo.
 */
export function coincide(f: ClienteConSaldo, q: string): boolean {
  const min = plano(q);
  const porTexto = [f.nombre, f.codigo, f.documento, f.telefono, f.email]
    .filter(Boolean)
    .some((v) => plano(String(v)).includes(min));
  if (porTexto) return true;
  const digitos = q.replace(/\D/g, "");
  if (/[^0-9 .+()-]/.test(q) || digitos.length < 3) return false;
  return [f.documento, f.telefono]
    .filter(Boolean)
    .some((v) => String(v).replace(/\D/g, "").includes(digitos));
}

export function SelectorCliente({
  abierto,
  onCerrar,
  onElegir,
  onCrearNuevo,
  textoInicial = "",
}: SelectorClienteProps): JSX.Element | null {
  const [texto, setTexto] = useState("");
  const [resultados, setResultados] = useState<ClienteConSaldo[]>([]);
  /** De que texto es la lista en pantalla: la busqueda llega 200 ms despues. */
  const [textoDeLista, setTextoDeLista] = useState<string | null>(null);
  /**
   * El texto con el que se apreto Enter antes de que llegara su lista. Se
   * resuelve cuando llega (ver el efecto de abajo de `onTecla`).
   */
  const enterPendiente = useRef<string | null>(null);
  /** Fila resaltada: la que se elige con Enter. */
  const [indice, setIndice] = useState(0);
  const listaRef = useRef<HTMLDivElement>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!abierto) {
      setTexto("");
      setResultados([]);
      setTextoDeLista(null);
      enterPendiente.current = null;
      setError(null);
      abortRef.current?.abort();
      return;
    }
    setTexto(textoInicial);

    /*
     * Las teclas que llegan ANTES de que el campo tenga el foco no se pierden.
     * El foco se pone a los 40 ms, y quien tipea rapido —o un lector de codigo
     * de barras— mandaba dos o tres letras en ese hueco: `PasoDestinatario` ya
     * no las toma porque el dialogo esta abierto, y el campo todavia no. «valen»
     * quedaba «ven», y Enter elegia a otro cliente.
     */
    //
    // Se acumula en una variable propia y no con `previo + tecla`: el
    // `setTexto(textoInicial)` de arriba corre en un efecto y la tecla llega
    // por un listener nativo, con mas prioridad: React dibujaba primero la
    // tecla sobre el texto vacio («a»), la tecla siguiente entraba al campo
    // sobre ese «a» y la «V» se perdia. «Valentin» quedaba «alentin», y Enter
    // podia elegir a otro cliente. Pasado el arranque (el foco de abajo), el
    // texto en pantalla ya es el bueno y se vuelve a sumar sobre el: si no,
    // una tecla despues de sacar el foco pisaria lo tipeado.
    let acumulado: string | null = textoInicial;
    const alTeclear = (e: KeyboardEvent): void => {
      const campo = inputRef.current;
      if (!campo || document.activeElement === campo) return;
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1) return;
      // El espacio sobre un boton es apretarlo, no escribir.
      if (e.key === " " && document.activeElement?.tagName === "BUTTON") return;
      e.preventDefault();
      if (acumulado !== null) {
        acumulado += e.key;
        setTexto(acumulado);
      } else {
        setTexto((previo) => previo + e.key);
      }
      campo.focus();
    };
    window.addEventListener("keydown", alTeclear, true);

    const t = setTimeout(() => {
      acumulado = null;
      inputRef.current?.focus();
      // El cursor al final: se sigue escribiendo el nombre desde donde quedo.
      const n = inputRef.current?.value.length ?? 0;
      inputRef.current?.setSelectionRange(n, n);
    }, 40);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", alTeclear, true);
    };
    // `textoInicial` a proposito fuera de las dependencias: solo importa el
    // valor que tenia al ABRIR. Incluirlo pisaria lo que la persona ya tipeo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;

    setCargando(true);
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      try {
        // Se lee de `/deudas` y no de `/clientes` porque esa vista ya trae el
        // saldo, la mora y el limite. Pedir la lista y despues el saldo de cada
        // uno serian N+1 consultas para mostrar una lista de cinco nombres.
        //
        // `todos=1` es indispensable: sin el, la ruta devuelve `v_alertas_mora`
        // —solo morosos y excedidos— y un cliente que paga en fecha NO SE PODIA
        // ELEGIR. Cuanto mejor pagaba, mas invisible era en esta pantalla.
        const q = texto.trim();
        const r = await api.get<{ filas: ClienteConSaldo[] }>(
          `/deudas?todos=1&limite=200${q ? `&buscar=${encodeURIComponent(q)}` : ""}`,
          { signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;

        const filas = r?.filas ?? [];
        // El mismo filtro que hace el servidor, repetido aca: cubre el instante
        // entre que se tipea y llega la respuesta. Por los MISMOS campos, para
        // que no filtre distinto que el backend y desaparezcan resultados que
        // el servidor si habia encontrado.
        const filtrados = q ? filas.filter((f) => coincide(f, q)) : filas;

        // Buscando, alfabetico: se esta apuntando a un nombre concreto y el
        // orden por deuda lo esconderia en el medio de la lista. Sin buscar,
        // los que deben primero: es a quienes hay que mirar con mas atencion
        // antes de sumarles otra venta.
        const ordenados = q
          ? [...filtrados].sort((a, b) => a.nombre.localeCompare(b.nombre, "es"))
          : [...filtrados].sort((a, b) => Number(b.saldo_ars_hoy) - Number(a.saldo_ars_hoy));

        setResultados(ordenados.slice(0, 40));
        setTextoDeLista(q);
        setError(null);
      } catch (e) {
        if (!ctrl.signal.aborted) {
          setError(mensajeDeError(e));
          setResultados([]);
        }
      } finally {
        if (!ctrl.signal.aborted) setCargando(false);
      }
    }, 200);

    return () => clearTimeout(t);
  }, [texto, abierto]);

  // Cada busqueda nueva vuelve a apuntar al primero: dejar el indice donde
  // estaba haria que Enter eligiera a un cliente que ya no esta en pantalla.
  useEffect(() => setIndice(0), [resultados]);

  const elegir = useCallback(
    (c: ClienteConSaldo) => {
      onElegir(c.cliente_id, c.nombre);
      onCerrar();
    },
    [onElegir, onCerrar],
  );

  /**
   * Flechas para moverse, Enter para elegir. Escape lo maneja Radix.
   *
   * Es el mismo teclado que el buscador de productos del ticket: en el
   * mostrador se carga una venta entera sin soltar el teclado, y tener que ir
   * al mouse solo para el cliente rompe el ritmo. Las flechas no salen de la
   * lista (se frenan arriba y abajo) en vez de dar la vuelta: pasarse de largo
   * y aparecer en la otra punta desorienta mas de lo que ayuda.
   */
  const onTecla = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const q = texto.trim();
        const elegido = resultados[indice];
        // La lista puede ser de lo que habia escrito hace un instante: la
        // busqueda espera 200 ms. Con «valen» + Enter rapido, elegia al primero
        // de la lista de «v». Si la lista no es de lo escrito y el resaltado
        // no coincide, el Enter queda pendiente y se resuelve cuando llega la
        // lista nueva. Antes se descartaba: quien tipea y aprieta Enter de una
        // —o un lector de codigo de barras— dejaba la venta sin cliente.
        if (elegido && (textoDeLista === q || coincide(elegido, q))) {
          elegir(elegido);
        } else if (textoDeLista !== q) {
          enterPendiente.current = q;
        }
        return;
      }

      if (resultados.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setIndice((i) => Math.min(i + 1, resultados.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setIndice((i) => Math.max(i - 1, 0));
      }
    },
    [resultados, indice, elegir, texto, textoDeLista],
  );

  /**
   * El Enter pendiente: cuando llega la lista de ESE texto, elige al primero
   * si coincide. Si despues del Enter se siguio escribiendo, ese Enter ya no
   * vale: era para otro texto.
   */
  useEffect(() => {
    const pendiente = enterPendiente.current;
    if (pendiente === null) return;
    if (pendiente !== texto.trim()) {
      enterPendiente.current = null;
      return;
    }
    if (textoDeLista !== pendiente) return;
    enterPendiente.current = null;
    const primero = resultados[0];
    if (primero && coincide(primero, pendiente)) elegir(primero);
  }, [resultados, textoDeLista, texto, elegir]);

  // La fila resaltada tiene que estar a la vista: con 40 resultados, bajar con
  // la flecha sin scrollear deja el resaltado abajo del borde.
  useEffect(() => {
    const fila = listaRef.current?.querySelector<HTMLElement>(`[data-indice="${indice}"]`);
    fila?.scrollIntoView({ block: "nearest" });
  }, [indice]);

  if (!abierto) return null;

  const busqueda = texto.trim();

  return (
    <Dialog
      open
      onOpenChange={(sigueAbierto) => {
        if (!sigueAbierto) onCerrar();
      }}
    >
      {/* Variante "paleta": anclada arriba, sin padding propio (la cabecera y
          el pie llevan el suyo) y con el cuerpo pegado a los bordes. */}
      <DialogContent
        tamano="md"
        mobile="hoja"
        className="gap-0 p-0 sm:top-[10vh] sm:max-h-[70vh] sm:translate-y-0 sm:p-0"
        // Radix enfocaria el campo seleccionando todo su contenido, y la
        // primera tecla borraria el `textoInicial`. El foco lo pone el efecto
        // de arriba, con el cursor al final.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="border-b border-border py-1 pl-1 pr-12">
          <DialogTitle className="sr-only">Elegir cliente</DialogTitle>
          <InputBusqueda
            ref={inputRef}
            apariencia="plano"
            etiqueta="Buscá el cliente por nombre"
            valor={texto}
            onValorChange={setTexto}
            cargando={cargando}
            onKeyDown={onTecla}
            role="combobox"
            aria-expanded={resultados.length > 0}
            aria-controls="lista-clientes"
            aria-activedescendant={
              resultados[indice] ? `cliente-${resultados[indice].cliente_id}` : undefined
            }
            spellCheck={false}
          />
        </DialogHeader>

        <DialogBody className="m-0 space-y-0 p-0 sm:mx-0 sm:px-0">
          {error && (
            <Aviso tono="destructive" compacto className="m-4">
              {error}
            </Aviso>
          )}

          {!cargando && !error && resultados.length === 0 && (
            <EstadoVacio
              tamano="sm"
              titulo={busqueda ? `No encontré a «${busqueda}».` : "No hay clientes cargados."}
            />
          )}

          <div ref={listaRef} id="lista-clientes" role="listbox">
            {resultados.map((c, i) => {
              const debe = Number(c.saldo_ars_hoy) > 0.009;
              const aFavor = Number(c.saldo_ars_hoy) < -0.009;
              const activo = i === indice;

              return (
                // <button> crudo: fila de listbox (`role="option"`), elegida con
                // flechas + Enter desde el combobox; no es un Button de accion.
                <button
                  key={c.cliente_id}
                  id={`cliente-${c.cliente_id}`}
                  data-indice={i}
                  role="option"
                  aria-selected={activo}
                  type="button"
                  onClick={() => elegir(c)}
                  // El mouse manda sobre el teclado: pasar por encima mueve el
                  // resaltado, para que Enter no elija una fila distinta de la
                  // que se esta mirando.
                  onMouseEnter={() => setIndice(i)}
                  className={cn(
                    "flex min-h-11 w-full items-center gap-3 border-b border-border px-4 py-3 text-left transition-colors last:border-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-6",
                    activo ? "bg-accent" : "hover:bg-accent",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{c.nombre}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                      <BadgeMora estado={c.estado_mora} />
                      {/* Un cliente con cuenta indexada al dolar tiene DOS numeros
                          legitimos, y el que se le cobra hoy es el de pesos. */}
                      {c.cc_modo === "USD" && (
                        <span className="text-muted-foreground">· cuenta en dólares</span>
                      )}
                      {c.excedido && (
                        <Badge variant="destructive">
                          <AlertTriangle aria-hidden="true" />
                          excedió su límite
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
                    {debe ? (
                      <>
                        <Importe monto={Number(c.saldo_ars_hoy)} moneda="ARS" />
                        <span className="text-xs text-muted-foreground">debe</span>
                      </>
                    ) : aFavor ? (
                      <>
                        <Importe monto={Math.abs(Number(c.saldo_ars_hoy))} moneda="ARS" />
                        <Badge variant="success">a favor</Badge>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">sin deuda</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </DialogBody>

        {/* Un boton, NO un link a /clientes.
            Con un link, dar de alta un cliente en medio de una venta navegaba
            fuera y se llevaba puesto el ticket entero: los productos cargados,
            los descuentos, los medios de pago. Y es justo el camino al que
            empuja un pago parcial, que exige cliente para dejar la deuda. */}
        <DialogFooter className="border-t border-border px-2 py-2 sm:justify-start">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onCrearNuevo(texto.trim())}
          >
            <UserPlus aria-hidden="true" />
            Dar de alta un cliente nuevo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
/**
 * Cinta del destinatario de la venta, adentro del ticket.
 *
 * ES UNA BANDA, NO UN CHIP
 * ------------------------
 * Antes era una pastilla de 12px debajo del titulo "Venta" con el nombre y
 * cuanto debia. Entraba en cualquier lado y por eso no se veia: el dato que
 * decide si conviene seguir cargando la venta estaba escrito mas chico que el
 * nombre de un producto.
 *
 * Ahora ocupa el ancho del ticket, que desde que la venta dejo de compartir la
 * pantalla con la columna de indicadores es ancho de sobra.
 *
 * QUE MUESTRA Y POR QUE
 * ---------------------
 * `v_mora_clientes` ya traia todo esto y la pantalla usaba dos campos. Los que
 * faltaban son justo los que cambian una decision:
 *
 *   credito disponible — `emitir_venta` RECHAZA la venta que excede el limite.
 *                        Verlo antes de cargar es la diferencia entre frenar a
 *                        tiempo y descubrirlo al apretar Cobrar.
 *   cuenta en dolares  — un cliente con `cc_modo = USD` tiene la deuda anclada
 *                        al dolar. Cambia que numero se le cobra hoy.
 *   antiguedad         — no es lo mismo deber hace tres dias que hace 90.
 *
 * El estado se lee del color ademas del texto: ambar reclama atencion, rojo
 * frena. Sin el color hay que leer cuatro numeros para saber si algo anda mal.
 * El tinte va en la tarjeta (`Card tono`) y en el badge de mora; los importes
 * llevan siempre el color de su moneda.
 */
export function ClienteDelTicket({
  clienteId,
  nombre,
  mostrador = false,
  cotizacion,
  onCambiar,
  onQuitar,
}: {
  clienteId: number | null;
  nombre: string | null;
  /** La venta se declaro de mostrador a proposito en el paso previo. */
  mostrador?: boolean;
  /** Para pasar el limite a la moneda del saldo. Ver `disponible`. */
  cotizacion: number;
  onCambiar: () => void;
  onQuitar: () => void;
}): JSX.Element {
  const [datos, setDatos] = useState<ClienteConSaldo | null>(null);
  // El cobro de la deuda, desde la venta misma (decidido el 05/11): antes había
  // que irse a Clientes, con el cliente adelante y el ticket a medio armar.
  const [cobrando, setCobrando] = useState<DeudorFila | null>(null);
  // Después de cobrar, el saldo de la tarjeta se vuelve a leer.
  const [lectura, setLectura] = useState(0);

  useEffect(() => {
    if (clienteId === null) {
      setDatos(null);
      return;
    }
    let vivo = true;
    void api
      .get<{ saldo?: ClienteConSaldo; mora?: ClienteConSaldo }>(`/clientes/${clienteId}`)
      .then((r) => {
        if (vivo) setDatos((r?.mora ?? r?.saldo ?? null) as ClienteConSaldo | null);
      })
      .catch(() => {
        /* que no se pueda leer el saldo no debe impedir vender */
      });
    return () => {
      vivo = false;
    };
  }, [clienteId, lectura]);

  // ── Mostrador: mismo peso visual, otro contenido ──
  if (clienteId === null && mostrador) {
    return (
      <Card variant="muted" padding="compacto" className="flex items-center gap-3">
        <Store className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground">Mostrador</div>
          <div className="text-xs text-muted-foreground">Venta sin cliente asignado</div>
        </div>
        <Button
          type="button"
          variant="dashed"
          size="sm"
          className="w-auto shrink-0"
          onClick={onCambiar}
        >
          Asignar cliente
        </Button>
      </Card>
    );
  }

  // ── Sin contestar todavia (tickets viejos, sin el campo `mostrador`) ──
  if (clienteId === null) {
    return (
      <Button type="button" variant="dashed" className="justify-start" onClick={onCambiar}>
        <UserPlus aria-hidden="true" />
        Asignar cliente
      </Button>
    );
  }

  const debe = datos ? Number(datos.saldo_ars_hoy) : 0;
  const tieneDeuda = debe > 0.009;
  const aFavor = debe < -0.009;
  const enDolares = datos?.moneda_anclaje === "USD";

  /**
   * Lo que le queda de credito, en la moneda de SU limite.
   *
   * El saldo esta anclado en una moneda y el limite puede estar en otra, asi
   * que hay una conversion en el medio — la misma que hace `v_mora_clientes`
   * en SQL para decidir `excedido`. El semaforo (rojo / no rojo) se toma del
   * `excedido` que calculo el servidor y no de esta cuenta: si las dos
   * difirieran por redondeo, manda la del backend, que es la que despues
   * frena o deja pasar la venta.
   */
  const limite = datos?.limite_credito == null ? null : Number(datos.limite_credito);
  const disponible =
    limite === null || !datos
      ? null
      : limite -
        convertir(Number(datos.saldo), datos.moneda_anclaje, datos.limite_moneda, cotizacion);

  const tono = datos?.excedido
    ? "destructive"
    : datos?.estado_mora === "MORA_90"
      ? "destructive"
      : datos?.estado_mora === "MORA_60" || datos?.estado_mora === "MORA_30"
        ? "warning"
        : "none";

  return (
    <Card
      variant={tono === "none" ? "muted" : "default"}
      tono={tono}
      padding="compacto"
      className="flex flex-wrap items-center gap-x-4 gap-y-2"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <User className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div className="min-w-0">
          <Button
            type="button"
            variant="link"
            className="max-w-full truncate font-semibold text-foreground"
            onClick={onCambiar}
            title="Cambiar el cliente"
          >
            {nombre}
          </Button>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            {/* «AL DÍA» pegado a una deuda se leia como «no debe nada»: con
                deuda y sin atraso se dice asi. */}
            {datos &&
              (datos.estado_mora === "AL_DIA" && tieneDeuda ? (
                <span className="text-muted-foreground">debe, sin atraso</span>
              ) : (
                <BadgeMora estado={datos.estado_mora} corto />
              ))}
            {/* Un cliente con la cuenta anclada al dolar tiene DOS numeros
                legitimos: la deuda en dolares y lo que son hoy en pesos. */}
            {datos?.cc_modo === "USD" && (
              <span className="text-muted-foreground">· cuenta en dólares</span>
            )}
            {datos?.antiguedad_deuda != null && datos.antiguedad_deuda > 0 && tieneDeuda && (
              <span className="text-muted-foreground">
                · la más vieja, {datos.antiguedad_deuda}{" "}
                {datos.antiguedad_deuda === 1 ? "día" : "días"}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Los numeros, a la derecha y en columnas ──
          En el celular van en su propio renglón, debajo del nombre: al lado,
          con una cuenta en dólares, el nombre quedaba de 100 px y se leía
          «ale Biot» con una palabra por renglón. */}
      {datos && (
        <div className="flex shrink-0 items-start gap-4 max-sm:order-2 max-sm:basis-full max-sm:justify-end">
          {/* En una cuenta en dolares el numero de la cuenta es el de
              dolares, y los pesos son lo que valen hoy. Antes se veia solo
              «debe $ 1.558.200» y no los US$ 980 que dice su cuenta. */}
          {enDolares ? (
            <Dato
              etiqueta={aFavor ? "a favor" : "debe"}
              monto={tieneDeuda || aFavor ? Math.abs(Number(datos.saldo)) : null}
              moneda="USD"
              nota={
                tieneDeuda || aFavor ? (
                  <>
                    hoy <Importe monto={Math.abs(debe)} moneda="ARS" tamano="sm" />
                  </>
                ) : null
              }
            />
          ) : (
            <Dato
              etiqueta={aFavor ? "a favor" : "debe"}
              monto={tieneDeuda || aFavor ? Math.abs(debe) : null}
              moneda="ARS"
            />
          )}
          {disponible !== null && (
            <Dato
              etiqueta={datos.excedido ? "excedido en" : "crédito disponible"}
              monto={Math.abs(disponible)}
              moneda={datos.limite_moneda}
            />
          )}
        </div>
      )}

      {datos?.excedido && (
        <Aviso tono="destructive" compacto className="w-full max-sm:order-2">
          {/* El limite avisa, no bloquea: una venta que deja deuda pide
              confirmacion antes de emitirse. «Va a ser rechazada» era falso. */}
          Excedió el límite de crédito. Una venta que deje deuda te va a pedir confirmación.
        </Aviso>
      )}

      <IconButton
        icono={X}
        tamano="sm"
        aria-label="Quitar el cliente de esta venta"
        className="shrink-0 max-sm:order-1"
        onClick={onQuitar}
      />

      {/* Es la fila de `v_mora_clientes`, la misma con la que cobra Clientes:
          el cobro es idéntico, con su caja, su moneda y su llave. */}
      {datos && tieneDeuda ? (
        <Button
          type="button"
          variant="outline"
          className="w-full max-sm:order-3"
          onClick={() => setCobrando(datos as unknown as DeudorFila)}
        >
          <HandCoins aria-hidden="true" />
          Cobrar deuda
        </Button>
      ) : null}

      {/* Montado solo al cobrar: lee cajas y cotización, y la tarjeta no las
          necesita para nada más. */}
      {cobrando ? (
        <CobroCliente
          cliente={cobrando}
          onCerrar={() => {
            setCobrando(null);
            setLectura((n) => n + 1);
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * Un importe con su etiqueta arriba, alineado a la derecha. El numero lleva el
 * color de su moneda; "excedido" o "a favor" lo dice la etiqueta, no el color.
 */
function Dato({
  etiqueta,
  monto,
  moneda,
  nota = null,
}: {
  etiqueta: string;
  monto: number | null;
  moneda: Moneda;
  /** Una linea chica debajo del numero: el equivalente de hoy en la otra moneda. */
  nota?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-end">
      <span className="text-xs leading-tight text-muted-foreground">{etiqueta}</span>
      {monto === null ? (
        <span className="text-sm text-muted-foreground">—</span>
      ) : (
        <Importe monto={monto} moneda={moneda} />
      )}
      {nota && <span className="text-xs leading-tight text-muted-foreground">{nota}</span>}
    </div>
  );
}
